import Vue from 'vue';
import { computed } from 'vue-demi';

import { useRouter } from './use-router';
import { useAuth } from './use-auth';
import { useGlobalVars } from './use-global-vars';
import { useAppLauncherIntegration } from './use-app-launcher-integration';
import { resolveRuntimeUiConfig } from '@/utils/runtime-ui-urls';
import { getUIConfig } from '@/utils/ui-config';

/** At the moment we maintain backwards compatibility with Action Desk */
export const useHelpers = () => {
  const { route } = useRouter();
  const { userId, accountId, getToken } = useAuth();
  const { isUsingLauncher } = useAppLauncherIntegration().getRefs();
  const $global = useGlobalVars().getVueGlobals();
  /** Allow using compiled config in case AD/Shared do not provide value through $helpers. E.g. sentryConfig */
  const $ORConfig = $global.$ORConfig || getUIConfig() || {};
  const runtimeUiConfig = resolveRuntimeUiConfig({
    AUTH_UI_ALIASES: $ORConfig.AUTH_UI_ALIASES || [],
    AUTH_UI_URL: (Vue.prototype.$helpers?.authUiUrl || $ORConfig.AUTH_UI_URL || '').replace(/FEATURE\./i, ''),
    COOKIE_DOMAIN: $ORConfig.COOKIE_DOMAIN || '',
    HITL_UI_ALIASES: $ORConfig.HITL_UI_ALIASES || [],
    HITL_UI_URL: Vue.prototype.$helpers?.hitlUiUrl || $ORConfig.HITL_UI_URL || '',
    SDK_API_ALIASES: $ORConfig.SDK_API_ALIASES || [],
    SDK_API_URL: (Vue.prototype.$helpers?.sdkApiUrl || $ORConfig.SDK_API_URL || '').replace(/FEATURE\./i, ''),
  });

  const viewId = computed(() => route.value.params?.id || '');

  const httpGatewayUrl = computed(() => {
    return (Vue.prototype.$helpers?.httpGatewayUrl || $ORConfig.GATEWAY_URL || '').replace(/FEATURE\./i, '');
  });
  const sdkApiUrl = computed(() => runtimeUiConfig.SDK_API_URL);
  const serviceDiscoveryURL = computed(() => {
    return (Vue.prototype.$helpers?.serviceDiscoveryUrl || $ORConfig.SERVICE_DISCOVERY_API_URL || '').replace(/FEATURE\./i, '');
  });
  const providersAccountId = computed(() => {
    return Vue.prototype.$helpers.providersAccountId
      ? Vue.prototype.$helpers.providersAccountId()
      : $ORConfig.PROVIDERS_ACCOUNT_ID;
  });


  /** New files API should use this value */
  const mediaURL = computed(() => {
    return Vue.prototype.$helpers?.filesEksApiUrl || $ORConfig.FILE_EKS_API_URL;
  });
  /** Backwards compatibility files URL */
  const filesApiUrl = computed(() => {
    return Vue.prototype.$helpers?.filesApiUrl || $ORConfig.FILE_API_URL;
  });

  const sentryConfig = computed(() => $ORConfig?.sentry || {});

  const emBusUrl = computed(() => {
    return Vue.prototype.$helpers?.emBusUrl || `wss://${$ORConfig.EM_BUS_DOMAIN}`;
  });
  const environmentEnv = computed(() => {
    return Vue.prototype.$helpers?.environmentEnv || $ORConfig.ENVIRONMENT;
  });
  const authUiUrl = computed(() => runtimeUiConfig.AUTH_UI_URL);
  const filesUiUrl = computed(() => {
    return (Vue.prototype.$helpers?.filesUiUrl || $ORConfig.FILE_UI_URL || '').replace(/FEATURE\./i, '');
  });
  const libraryAccountId = computed(() => {
    return Vue.prototype.$helpers?.libraryAccountId || $ORConfig.TEMPLATES_ACCOUNT_ID;
  });
  const environmentClient = computed(() => {
    return Vue.prototype.$helpers?.environmentClient || $ORConfig.CLIENT;
  });

  const actionDeskURL = computed(() => {
    return (Vue.prototype.$helpers?.actionDeskUrl || $ORConfig.REPORTING_URL || '').replace(/FEATURE\./i, '');
  });
  const contactsURL = computed(() => Vue.prototype.$helpers?.contactsApiUrl || $ORConfig.CONTACTS_API_URL || '');
  const usersURL = computed(() => (Vue.prototype.$helpers?.usersApiUrl || $ORConfig.USERS_API_URL || '').replace(/FEATURE\./i, ''));
  const humanInTheLoopURL = computed(() => runtimeUiConfig.HITL_UI_URL);
  const hitlApiURL = computed(() => Vue.prototype.$helpers?.hitlApiUrl || $ORConfig.HITL_API_URL || '');
  const hitlEKSApiURL = computed(() => Vue.prototype.$helpers?.hitlEksApiUrl || $ORConfig.HITL_EKS_API_URL || '');
  const dataHubURL = computed(() => (Vue.prototype.$helpers?.dataHubUrl || $ORConfig.DATA_HUB_URL || '').replace(/FEATURE\./i, ''));

  const webrtcDomain = computed((): string => {
    return Vue.prototype.$helpers?.webrtcDomain || $ORConfig.WEBRTC_DOMAIN;
  });

  const webrtcWSSAddress = computed((): string => {
    return Vue.prototype.$helpers?.webrtcWssAddress
      || $ORConfig?.getWebRTCAddress();
  });

  const getGatewayURL = (key: string, accountId: string): string => {
    return Vue.prototype.$helpers?.gatewayUrl
      ? Vue.prototype.$helpers.gatewayUrl(key, accountId)
      : $ORConfig?.getHTTPGatewayURL(key, accountId);
  };

  const getRegisterComponentApi = () => {
    if (Vue.prototype.$helpers?.getRegisterComponentApi) {
      return Vue.prototype.$helpers?.getRegisterComponentApi();
    }

    /* Return a dummy response so HITL app won't crash */
    return {
      getCommonArgs: () => ({
        viewMessageBus: new Vue(),
        cardMessageBus: new Vue(),
      }),
    };
  };

  const onSignOut = async () => {
    if (!isUsingLauncher.value) {
      return Vue.prototype.$helpers?.signOut({ viewId: viewId.value });
    }

    const logoutUrl = runtimeUiConfig.AUTH_UI_URL
      ? `${runtimeUiConfig.AUTH_UI_URL}/logout?redirectPath=${encodeURIComponent(window.location.origin)}`
      : '';

    if (logoutUrl) {
      window.location.href = logoutUrl;
      return;
    }

    await $global.$ORAuthAPI.signOut(getToken());
    window.location.reload();
  };


  return {
    userId,
    viewId,
    getToken,
    emBusUrl,
    httpGatewayUrl,
    sdkApiUrl,
    providersAccountId,
    serviceDiscoveryURL,
    environmentClient,
    environmentEnv,
    sentryConfig,
    authUiUrl,
    libraryAccountId,
    accountId,
    mediaURL,
    filesApiUrl,
    filesUiUrl,
    webrtcDomain,
    webrtcWSSAddress,
    humanInTheLoopURL,
    hitlApiURL,
    hitlEKSApiURL,
    actionDeskURL,
    contactsURL,
    usersURL,
    dataHubURL,
    getGatewayURL,
    getRegisterComponentApi,
    onSignOut
  };
};
